import React, { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppContext } from '../../App';
import { authAxios } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Sparkles, AlertTriangle } from 'lucide-react';

export default function AdminLeagueOptions() {
  const { API } = useContext(AppContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [flags, setFlags] = useState({ ai_event_drafting_enabled: false });
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await authAxios.get(`${API}/admin/league-options`);
        setFlags(data || {});
        setLoadError(null);
      } catch (e) {
        setLoadError(e?.response?.data?.detail || 'Failed to load league options');
      } finally {
        setLoading(false);
      }
    })();
  }, [API]);

  const toggleFlag = async (key, value) => {
    setSaving(true);
    try {
      const { data } = await authAxios.patch(`${API}/admin/league-options`, { [key]: value });
      setFlags(data);
      toast.success(value ? 'Enabled' : 'Disabled');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 mb-2">
            <ArrowLeft className="w-3 h-3" /> Back to Admin
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-800">League Options</h1>
          <p className="text-slate-600 text-sm">Beta features and per-league toggles.</p>
        </div>
      </div>

      {flags.ai_event_drafting_visible ? (
        <Card data-testid="league-options-ai-drafting-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-600" />
              AI Event Drafting
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                {flags.ai_event_drafting_mode === 'closed_beta' ? 'CLOSED BETA' : 'BETA'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadError && (
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-sm">
                <strong>Heads up:</strong> {loadError}
                {loadError.includes('tenant') && ' — Super Admins should switch to a specific league before editing its options.'}
              </div>
            )}
            <p className="text-sm text-slate-700">
              Automatically search the internet for Survivor episode results 12 hours after an episode
              airs, then draft event log entries for each contestant based on this league's scoring
              rules. <strong>All AI-drafted entries require admin review before they affect live
              scores.</strong>
            </p>

            {flags.ai_event_drafting_enabled && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <div>
                  <strong>Beta feature.</strong> The AI may miss events, mis-identify contestants, or
                  generate inaccurate suggestions. Always review drafts on the Event Log page before
                  approving. Drafted entries live in a "Pending Review" section above the main event log.
                </div>
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <div className="text-sm font-medium">Enable AI Event Drafting</div>
                <div className="text-xs text-slate-500">
                  {flags.ai_event_drafting_enabled
                    ? 'On — AI will auto-draft events for new episodes (12h after air)'
                    : 'Off — no AI activity'}
                </div>
              </div>
              <Switch
                checked={!!flags.ai_event_drafting_enabled}
                onCheckedChange={(v) => toggleFlag('ai_event_drafting_enabled', v)}
                disabled={loading || saving || !flags.ai_event_drafting_editable}
                data-testid="toggle-ai-event-drafting"
              />
            </div>
          </CardContent>
        </Card>
      ) : (
        !loading && (
          <Card>
            <CardContent className="py-10 text-center text-sm text-slate-500">
              No beta features are currently available for this league.
            </CardContent>
          </Card>
        )
      )}
    </div>
  );
}
